import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// ngrx store
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
// import 'rxjs/add/operator/catch';
// import 'rxjs/add/operator/map';
// import 'rxjs/add/observable/throw';
import { throwError } from 'rxjs';


// services
import { ErrorHandler } from '../../shared/services/error-handler.service';
import { SharedService } from '../../shared/services/shared-service.service';

// models
import { AppState } from '../models/app-state.interface';
import { GetTimeslots } from '../models/get-timeslots.interface';
import { ReserveTimeslot } from '../models/reserve-timeslot.interface';

// globals
import {
  API_PATH,
  SUBSCRIPTION_KEY,
  GET_GOOGLE_LOCATION,
  GET_LOCATION,
  SAVE_GOOGLE_LOCATION,
  GENERATE_TIMESLOTS,
  RESERVE_TIMESLOTS,
  VALIDATE_GEO_COORDS,
  GET_POST_OFFICE,
} from '../../shared/globals';
import { GetPostOfficeDetails } from '../models/location.interface';
import moment from 'moment';
import { updatePlaceId, zoneInfoUpdate } from '../reducers/booking.actions';
import { panelSet } from '../reducers/panel.reducer';

interface GetGoogleRequest {
  userId: null;
  googlePlaceId: string;
}

interface ValidateGeoCoordRequest {
  airportCode: string;
  airline: string;
  geoCoord: {
      latitude: number;
      longitude: number;
  };
  slotNumber?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CollectionService {

  private _getLocationSubscription: Subscription;
  private _saveLocationSubscription: Subscription;
  private _validateGeoCoordsSubscription: Subscription;

  private _getLocationRequest: GetGoogleRequest = { userId: null, googlePlaceId: '' };
  private _reserveTimeslotRequest: ReserveTimeslot = { timeslot: '', date: '', bookingReferenceId: '', timeSlotId: '', overrideTimeSlotCapacity: null, departureAirportCode: '' };
  private _getTimeslotsRequest: GetTimeslots = {
    addOnProductType: [],
    journeyDirection: 1,
    airportCode: '',
    requestFlightNumber: ''
  };
  private postOfficeLocationsRequest: GetPostOfficeDetails;
  private postOfficeLocationsResponse = null;

  public postcodes;
  public postcodeSubject$: BehaviorSubject<any> = new BehaviorSubject({ hasError: true, data: null });
  public postcodeInZoneSubject$: Subject<any> = new Subject();
  public locationSubject$: Subject<any> = new Subject();
  public geoCoordsSubject$: Subject<any> = new Subject();
  public locationSaveSubject$: Subject<any> = new Subject();
  public timeslotSubject$: Subject<any> = new Subject();
  public timeslotAirportSubject$: Subject<any> = new Subject();
  public timeslotReserveSubject$: Subject<any> = new Subject();
  public postOfficeResponeSubject$: Subject<any> = new Subject();
  public locationDetails: Object;
  public blockedPostcodes: any;

  constructor(
    private _http: HttpClient,
    private _appStore: Store<AppState>,
    private _errorService: ErrorHandler,
    private _sharedService: SharedService
  ) { }

  getLocation(placeId) {

    if (typeof placeId === 'undefined') {
      this.locationSubject$.next('no place id');
      return;
    }

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    this._appStore.dispatch(updatePlaceId({ payload: placeId }));
    this._getLocationRequest.googlePlaceId = placeId;
    this._getLocationSubscription = this._http.post(API_PATH + GET_GOOGLE_LOCATION, this._getLocationRequest, { headers: headers })
      .subscribe(
        res => {
          this.locationSubject$.next(res);
          this._getLocationSubscription.unsubscribe();
        },
        err => {
          this.locationSubject$.next(err);
          this._errorService.handleError(err);
          this._getLocationSubscription.unsubscribe();
          return;
        });

  };

  getLocationByProvider(request, isBillingAddress = false) {

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const { airline, ...modifiedRequest } = request;
    this._getLocationSubscription = this._http.post(API_PATH + GET_LOCATION, modifiedRequest, { headers: headers })
      .subscribe(
        (res: any) => {

          // error throws when allies credits run out....
          if (res.validationErrors.length > 0) {
            this.locationSubject$.next('error');
            this._getLocationSubscription.unsubscribe();
          } else {
            const zoneInfo = JSON.parse(JSON.stringify(res.zoneInfo));
            this._appStore.dispatch(zoneInfoUpdate({ payload: zoneInfo }));
            this.locationSubject$.next(res);
            if (request.locationType === 4 && !isBillingAddress && res.location.geoCoord) {
              this.validatLocationGeoCoords({
                airline: request.airline,
                airportCode: request.airportCode,
                geoCoord: res.location.geoCoord
              })
            }
            this._getLocationSubscription.unsubscribe();
          }
        },
        err => {
          this.locationSubject$.next(err);
          this._errorService.handleError(err);
          this._getLocationSubscription.unsubscribe();
          return;
        });

  };

  validatLocationGeoCoords(request: ValidateGeoCoordRequest) {
    request.slotNumber = this._sharedService.getCurrentSlotNumber();

    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    this._validateGeoCoordsSubscription = this._http.post(API_PATH + VALIDATE_GEO_COORDS, request, { headers: headers })
      .subscribe(
        (res: any) => {
          if (!res.isGeoCoordValid) {
            this.geoCoordsSubject$.next({isValid: false, isCalled: true});
            this._validateGeoCoordsSubscription.unsubscribe();
          } else {
            const zoneInfo = JSON.parse(JSON.stringify(res.zoneInfo));
            this._appStore.dispatch(zoneInfoUpdate({ payload: zoneInfo }));
            this.geoCoordsSubject$.next({isValid: true, isCalled: true, response: res});
            this._validateGeoCoordsSubscription.unsubscribe();
          }
        },
        err => {
          this.locationSubject$.next(err);
          this._errorService.handleError(err);
          this._getLocationSubscription.unsubscribe();
          return;
        }
    )

  }

  saveLocation(locationObj) {
    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    this._saveLocationSubscription = this._http.post(API_PATH + SAVE_GOOGLE_LOCATION, locationObj, { headers: headers })
      .subscribe(
        res => {
          this.locationSaveSubject$.next(res);
          this._saveLocationSubscription.unsubscribe();
        },
        err => {
          this.locationSaveSubject$.next(err);
          this._errorService.handleError(err);
          this._saveLocationSubscription.unsubscribe();
          return;
        });
  }

  getTimeslots(request: GetTimeslots, isAirportTimeslot?) {

    this._getTimeslotsRequest = request;

    let apiResponse = {};

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    this._http.post(API_PATH + GENERATE_TIMESLOTS, this._getTimeslotsRequest, { headers: headers })
      .subscribe(
        (res: any) => {

          if (res.validationErrorMessages !== null) {

            if (res.validationErrorMessages[0].code === 760) {

              const errorData = {
                text: res.validationErrorMessages[0].reasons[0],
                type: 'info',
                sticky: false
              };
              this._errorService.handleCustomInfo(errorData);

            } else {

              const errorData = {
                text: res.validationErrorMessages[0].reasons[0],
                sticky: false
              };
              this._errorService.handleCustomError(errorData);
              this._errorService.removeFlightData$.next(true);

              setTimeout(() => {
                if (request.addOnProductType.length > 0) {
                  this._appStore.dispatch(panelSet({ payload: 1 }));
                  // console.log('collection service error!');
                } else {
                  this._appStore.dispatch(panelSet({ payload: 1 }));
                  // console.log('collection service error!');
                }
              }, 2500);
              return;

            }
          }

          apiResponse = res;

          if (!isAirportTimeslot) {
            this.timeslotSubject$.next(apiResponse);
          } else {
            this.timeslotAirportSubject$.next(apiResponse);
          }
        },
        err => {
          this.timeslotSubject$.next(err);
          this._errorService.handleError(err);
          return;
        }
      );

  }

  filterTimeslotItems(timeslotArr) {

    const filteredTimeslots = [];

    for (const item of timeslotArr) {

      let timeslotHolder = [];

      for (const timeslot of item.timeslots) {
        if (timeslot.remainingTimeSlots !== 0) {
          timeslotHolder.push(timeslot);
        }
      }

      item.timeslots = timeslotHolder;
      timeslotHolder = [];

      filteredTimeslots.push(item);
    }

    return filteredTimeslots;

  };

  reserveTimeslot(request) {

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    this._reserveTimeslotRequest = request;

    this._http.post(API_PATH + RESERVE_TIMESLOTS, this._reserveTimeslotRequest, { headers: headers })
      .subscribe(
        res => {
          this.timeslotReserveSubject$.next(res);
        },
        err => {
          this.timeslotReserveSubject$.next(err);
          this._errorService.handleError(err);
          return;
        }
      );

  }

  reserveTimeslotPromise(request) {

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    this._reserveTimeslotRequest = request;
    const promise = new Promise((resolve, reject) => {
      this._http
        .post(API_PATH + RESERVE_TIMESLOTS, this._reserveTimeslotRequest, { headers: headers })
        .toPromise()
        .then(
          (res: any) => { // Success
            resolve(res);
          }
        )
        .catch(
          err => {
            // console.log('API ERROR', err);
            resolve(err);
            this._errorService.handleError(err);
            return;
          }
        );

    });

    return promise;

  }

  timeslotAirport(date: string) {
    this.timeslotAirportSubject$.next(date);
  }

  getPostOffices(request: GetPostOfficeDetails) {
    if (JSON.stringify(this.postOfficeLocationsRequest) === JSON.stringify(request) && moment(this.postOfficeLocationsResponse?.responseTime).diff(moment(), 'minutes') <= 5) {
      this.postOfficeResponeSubject$.next(this.postOfficeLocationsResponse);
      return;
    }

    this.postOfficeLocationsRequest = request;
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    this._http.post(API_PATH + GET_POST_OFFICE, request, { headers: headers })
      .subscribe((res: any) => {
        if (res.validationErrors.length > 0 ||  res.responseStatus === 'Error') {
          this._errorService.handleError(res);
          this.postOfficeResponeSubject$.next('error');
          return;
        }

        if (res.postOfficeDetails.length > 0) {
          this.postOfficeLocationsResponse = { ...res, responseTime: moment().toISOString() };
        }
        this.postOfficeResponeSubject$.next(res);
      }, err => {
        // console.log(err);
        this._errorService.handleError(err);
      })
  }

}
